import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Upload, Target, FileText, GitCompareArrows, CalendarRange, CheckCircle2, ArrowRight, X, Sparkles, Compass, Briefcase, TrendingUp, AlertTriangle } from "lucide-react";

const STEPS = ["Goal", "Resume", "Skill Gap", "Timeline", "Generate"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const fileRef = useRef();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [roleTarget, setRoleTarget] = useState("");
  const [salary, setSalary] = useState("");
  const [skills, setSkills] = useState([]);
  const [fileName, setFileName] = useState("");
  const [goal, setGoal] = useState(null);
  const [chosenDays, setChosenDays] = useState(30);
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [interests, setInterests] = useState("");
  const [suggested, setSuggested] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [generateError, setGenerateError] = useState("");

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));

  const discoverRoles = async () => {
    setDiscovering(true);
    try {
      const { data } = await api.post("/roles/suggest", { target_salary: salary, interests });
      setSuggested(data.roles || []);
      if (!data.roles?.length) toast.error("No suggestions — add more about your interests.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setDiscovering(false); }
  };

  const pickRole = (title) => { setRoleTarget(title); setShowDiscovery(false); toast.success(`Targeting: ${title}`); };

  const handleGoal = () => {
    if (!roleTarget.trim()) { toast.error("Enter a target role"); return; }
    next();
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setLoading(true); setFileName(file.name);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/resume/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSkills(data.skills);
      toast.success(`Found ${data.skills.length} skills in your resume`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const analyzeGap = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/goals", { role_target: roleTarget, target_salary: salary, current_skills: skills });
      setGoal(data);
      setChosenDays(data.ai_recommended_days);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const generate = async () => {
    setLoading(true); setGenerateError("");
    try {
      await api.put(`/goals/${goal.goal_id}`, { user_chosen_days: chosenDays });
      await api.post("/roadmap/generate", { goal_id: goal.goal_id, total_days: chosenDays });
      await refreshUser();
      toast.success("Your roadmap is ready!");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      setGenerateError(msg);
      toast.error(msg);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain px-5 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <span className="font-heading font-extrabold text-xl tracking-tighter">adyap<span className="text-primary">.</span></span>
          <span className="text-xs text-zinc-500">Step {step + 1} of {STEPS.length}</span>
        </div>

        {/* progress */}
        <div className="flex gap-2 mb-10">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-white/10"}`} />
          ))}
        </div>

        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
          {step === 0 && (
            <div data-testid="onboard-goal-step">
              <Target className="w-8 h-8 text-primary mb-4" />
              <h1 className="font-heading text-3xl tracking-tighter font-extrabold">What's your career goal?</h1>
              <p className="text-zinc-400 mt-2">Tell us the exact role you're aiming for.</p>
              <div className="mt-8 space-y-4">
                <div>
                  <label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Target role</label>
                  <input data-testid="goal-role-input" value={roleTarget} onChange={(e) => setRoleTarget(e.target.value)}
                    placeholder="e.g. Senior Backend Engineer" className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-primary" />
                </div>
                <div>
                  <label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Target salary in rupees (optional)</label>
                  <input data-testid="goal-salary-input" value={salary} onChange={(e) => setSalary(e.target.value)}
                    placeholder="e.g. ₹25 LPA or ₹25,00,000" className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-primary" />
                </div>
              </div>

              <div className="mt-6">
                {!showDiscovery ? (
                  <button data-testid="open-discovery-btn" onClick={() => setShowDiscovery(true)}
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
                    <Compass className="w-4 h-4" /> Not sure which role? Let AI suggest roles for your target salary
                  </button>
                ) : (
                  <div className="glass rounded-2xl p-5" data-testid="discovery-panel">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading text-base font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> Role discovery</h3>
                      <button onClick={() => setShowDiscovery(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
                    </div>
                    <p className="text-sm text-zinc-400 mt-1">Tell us your interests; we'll match roles to your <span className="text-white">{salary || "target salary"}</span> goal.</p>
                    <textarea data-testid="interests-input" value={interests} onChange={(e) => setInterests(e.target.value)} rows={2}
                      placeholder="e.g. I enjoy data, problem solving, and building products" className="mt-3 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-primary resize-none" />
                    <button data-testid="suggest-roles-btn" onClick={discoverRoles} disabled={discovering}
                      className="mt-3 inline-flex items-center gap-2 bg-primary hover:bg-primary/90 px-5 py-2.5 rounded-xl font-medium transition-colors disabled:opacity-60">
                      {discovering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Suggest roles
                    </button>
                    {suggested.length > 0 && (
                      <div className="mt-4 space-y-2" data-testid="suggested-roles">
                        {suggested.map((r, i) => (
                          <button key={r.role_title || i} data-testid={`suggested-role-${i}`} onClick={() => pickRole(r.role_title)}
                            className="w-full text-left border border-white/10 hover:border-primary/50 rounded-xl p-4 transition-colors">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium flex items-center gap-2"><Briefcase className="w-4 h-4 text-primary" /> {r.role_title}</span>
                              <span className="text-xs text-zinc-300">{r.typical_salary_range}</span>
                            </div>
                            <p className="text-sm text-zinc-400 mt-1">{r.why_fit}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className={`text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-full ${r.demand === "high" ? "text-emerald-400 bg-emerald-400/10" : r.demand === "medium" ? "text-amber-400 bg-amber-400/10" : "text-zinc-400 bg-white/5"}`}>{r.demand} demand</span>
                              {r.entry_friendly && <span className="text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-full text-primary bg-primary/10">fresher-friendly</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <button data-testid="goal-next-btn" onClick={handleGoal} className="mt-8 inline-flex items-center gap-2 bg-primary hover:bg-primary/90 px-6 py-3 rounded-xl font-medium transition-colors">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {step === 1 && (
            <div data-testid="onboard-resume-step">
              <FileText className="w-8 h-8 text-primary mb-4" />
              <h1 className="font-heading text-3xl tracking-tighter font-extrabold">Upload your resume</h1>
              <p className="text-zinc-400 mt-2">We'll extract your current skills with AI. PDF, DOCX or TXT.</p>
              <input ref={fileRef} type="file" accept=".pdf,.docx,.txt" className="hidden" data-testid="resume-file-input"
                onChange={(e) => handleUpload(e.target.files?.[0])} />
              <button onClick={() => fileRef.current?.click()} disabled={loading} data-testid="resume-upload-btn"
                className="mt-8 w-full border-2 border-dashed border-white/15 hover:border-primary/50 rounded-2xl py-12 flex flex-col items-center gap-3 transition-colors disabled:opacity-60">
                {loading ? <Loader2 className="w-7 h-7 animate-spin text-primary" /> : <Upload className="w-7 h-7 text-zinc-400" />}
                <span className="text-sm text-zinc-400">{fileName || "Click to upload your resume"}</span>
              </button>
              {skills.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs tracking-wider uppercase font-semibold text-zinc-500 mb-3">Detected skills</p>
                  <div className="flex flex-wrap gap-2" data-testid="detected-skills">
                    {skills.map((s) => (
                      <span key={s} className="text-sm bg-white/5 border border-white/10 rounded-full px-3 py-1 flex items-center gap-1">
                        {s}
                        <button onClick={() => setSkills(skills.filter((x) => x !== s))} className="text-zinc-500 hover:text-white"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-8 flex gap-3">
                <button onClick={() => { setSkills([]); setFileName(""); next(); }} className="text-sm text-zinc-400 hover:text-white px-4 py-3">Skip for now</button>
                <button data-testid="resume-next-btn" onClick={next} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 px-6 py-3 rounded-xl font-medium transition-colors">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboard-gap-step">
              <GitCompareArrows className="w-8 h-8 text-primary mb-4" />
              <h1 className="font-heading text-3xl tracking-tighter font-extrabold">Analyze your skill gap</h1>
              <p className="text-zinc-400 mt-2">AI will compare your skills against what <span className="text-white">{roleTarget}</span> requires.</p>
              {!goal ? (
                <button data-testid="analyze-gap-btn" onClick={analyzeGap} disabled={loading}
                  className="mt-8 inline-flex items-center gap-2 bg-primary hover:bg-primary/90 px-6 py-3 rounded-xl font-medium transition-colors disabled:opacity-60">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompareArrows className="w-4 h-4" />} Run skill-gap analysis
                </button>
              ) : (
                <div className="mt-8 space-y-5">
                  <div className="glass rounded-2xl p-5">
                    <p className="text-xs tracking-wider uppercase font-semibold text-zinc-500 mb-3">Skills to acquire</p>
                    <div className="flex flex-wrap gap-2">
                      {goal.skill_gaps.map((s) => <span key={s} className="text-sm bg-accent/15 text-accent border border-accent/30 rounded-full px-3 py-1">{s}</span>)}
                    </div>
                  </div>
                  <div className="glass rounded-2xl p-5">
                    <p className="text-xs tracking-wider uppercase font-semibold text-zinc-500 mb-3">Already strong in</p>
                    <div className="flex flex-wrap gap-2">
                      {(goal.current_skills.length ? goal.current_skills : ["—"]).map((s) => <span key={s} className="text-sm bg-white/5 border border-white/10 rounded-full px-3 py-1">{s}</span>)}
                    </div>
                  </div>
                  {goal.market && (goal.market.salary_range || goal.market.reality_note) && (
                    <div className="glass rounded-2xl p-5" data-testid="market-card">
                      <p className="text-xs tracking-wider uppercase font-semibold text-zinc-500 mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary" /> Market reality check</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-[10px] tracking-wider uppercase text-zinc-500">Typical salary</div>
                          <div className="text-sm font-medium mt-0.5">{goal.market.salary_range || "—"}</div>
                        </div>
                        <div>
                          <div className="text-[10px] tracking-wider uppercase text-zinc-500">Hiring demand</div>
                          <div className="text-sm font-medium mt-0.5 capitalize">{goal.market.demand || "—"}</div>
                        </div>
                      </div>
                      {goal.market.openings_note && <p className="text-sm text-zinc-400 mt-3">{goal.market.openings_note}</p>}
                      {goal.market.reality_note && (
                        <div className={`mt-3 flex items-start gap-2 rounded-xl p-3 ${goal.market.target_realistic ? "bg-emerald-400/10 border border-emerald-400/20" : "bg-amber-400/10 border border-amber-400/20"}`}>
                          {goal.market.target_realistic ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />}
                          <p className="text-sm text-zinc-200">{goal.market.reality_note}</p>
                        </div>
                      )}
                    </div>
                  )}
                  <button data-testid="gap-next-btn" onClick={next} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 px-6 py-3 rounded-xl font-medium transition-colors">
                    Pick a timeline <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {step === 3 && goal && (
            <div data-testid="onboard-timeline-step">
              <CalendarRange className="w-8 h-8 text-primary mb-4" />
              <h1 className="font-heading text-3xl tracking-tighter font-extrabold">Choose your timeline</h1>
              <p className="text-zinc-400 mt-2">AI recommends <span className="text-primary font-semibold">{goal.ai_recommended_days} days</span>. {goal.rationale}</p>
              <div className="mt-8 glass rounded-2xl p-6">
                <div className="flex items-baseline justify-between">
                  <span className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Plan length</span>
                  <span className="font-heading text-3xl font-extrabold" data-testid="chosen-days">{chosenDays} days</span>
                </div>
                <input data-testid="timeline-slider" type="range" min={14} max={120} value={chosenDays} onChange={(e) => setChosenDays(Number(e.target.value))}
                  className="w-full mt-4 accent-emerald-500" />
                <div className="flex justify-between text-xs text-zinc-500 mt-1"><span>14 (intense)</span><span>120 (relaxed)</span></div>
              </div>
              <button data-testid="timeline-next-btn" onClick={next} className="mt-8 inline-flex items-center gap-2 bg-primary hover:bg-primary/90 px-6 py-3 rounded-xl font-medium transition-colors">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {step === 4 && goal && (
            <div data-testid="onboard-generate-step" className="text-center py-6">
              <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-4" />
              <h1 className="font-heading text-3xl tracking-tighter font-extrabold">Ready to build your roadmap</h1>
              <p className="text-zinc-400 mt-2">A {chosenDays}-day plan to become a <span className="text-white">{roleTarget}</span>, with daily tasks and curated resources.</p>
              <button data-testid="generate-roadmap-btn" onClick={generate} disabled={loading}
                className="mt-8 inline-flex items-center gap-2 bg-primary hover:bg-primary/90 px-8 py-4 rounded-xl font-medium transition-colors disabled:opacity-60 glow-blue">
                {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating your plan…</> : (generateError ? <>Try again <ArrowRight className="w-4 h-4" /></> : <>Generate my roadmap <ArrowRight className="w-4 h-4" /></>)}
              </button>
              {loading && <p className="text-xs text-zinc-500 mt-4">This can take up to a minute while the AI designs each day.</p>}
              {!loading && generateError && (
                <div data-testid="generate-error-box" className="mt-6 mx-auto max-w-md text-left glass rounded-2xl border-amber-400/25 p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-200">The AI didn&apos;t respond in time.</p>
                    <p className="text-zinc-400 mt-1">{generateError}</p>
                    <p className="text-zinc-500 mt-2 text-xs">This usually clears up in a few seconds — the retry above will kick off another attempt.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
